import { useEffect, useMemo, useState } from "react";
import {
  siListQuestions, siMeta, siCreateQuestion, siUpdateQuestion,
  siDeleteQuestion, siReorderQuestions,
} from "../lib/smartInspectApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Search } from "lucide-react";

// --- Step metadata (Level 1) ---
const STEPS = [
  { key: "step1", num: "Step 1", name: "Company and Operations" },
  { key: "step2", num: "Step 2", name: "Current Inspection Process" },
  { key: "step3", num: "Step 3", name: "SmartInspect and Investment" },
];
const STEP_ORDER = STEPS.map((s) => s.key);
const STEP_MAP = Object.fromEntries(STEPS.map((s) => [s.key, s]));
const STEP_TITLES = Object.fromEntries(STEPS.map((s) => [s.key, `${s.num}: ${s.name}`]));

const humanize = (k) => String(k || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const EMPTY = {
  step: "step1", section: "General", field_key: "", label: "", input_type: "text",
  description: "", help_text: "", required: false, active: true, visible: true,
  default_value: "", min_value: "", max_value: "", unit: "", placeholder: "",
  options: [], roi_profile: "", customer_visible: true, customer_editable: true,
  consultant_editable: true, defaulted_by_admin: false, display_order: 0,
};

// Required badge pill
function RequiredBadge({ required }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${required ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
      {required ? "Required" : "Optional"}
    </span>
  );
}

// Visibility badge pill
function VisibilityBadge({ visible }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${visible ? "bg-[#E0F2FE] text-[#025979]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
      {visible ? "Visible" : "Hidden"}
    </span>
  );
}

// Status badge pill
function StatusBadge({ active }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function SmartInspectQuestionsPage() {
  const [questions, setQuestions] = useState([]);
  const [meta, setMeta] = useState({ input_types: [], steps: [], currencies: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Navigation + filters
  const [activeStep, setActiveStep] = useState("step1");
  const [activeSection, setActiveSection] = useState(null);
  const [search, setSearch] = useState("");
  const [fType, setFType] = useState("all");
  const [fRequired, setFRequired] = useState("all");
  const [fVisibility, setFVisibility] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [q, m] = await Promise.all([siListQuestions(), siMeta()]);
      setQuestions(q); setMeta(m);
    } catch { toast.error("Failed to load questions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Per-step question counts (Level 1 tab badges)
  const totalsByStep = useMemo(() => {
    const t = { step1: 0, step2: 0, step3: 0 };
    for (const q of questions) if (t[q.step] != null) t[q.step] += 1;
    return t;
  }, [questions]);

  // Section tree for the active step (Level 2 sidebar), derived dynamically.
  const sections = useMemo(() => {
    const forStep = questions.filter((q) => q.step === activeStep);
    const secs = {};
    for (const q of forStep) {
      const sec = q.section || "General";
      const cur = secs[sec] || { count: 0, minOrder: Infinity };
      cur.count += 1;
      cur.minOrder = Math.min(cur.minOrder, q.display_order || 0);
      secs[sec] = cur;
    }
    return Object.entries(secs)
      .map(([key, m]) => ({ key, label: humanize(key), count: m.count, minOrder: m.minOrder }))
      .sort((a, b) => (a.minOrder - b.minOrder) || a.label.localeCompare(b.label));
  }, [questions, activeStep]);

  // Keep the active section valid whenever the tree changes.
  useEffect(() => {
    if (!sections.length) { setActiveSection(null); return; }
    if (!sections.find((s) => s.key === activeSection)) setActiveSection(sections[0].key);
  }, [sections]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableTypes = useMemo(() => {
    const set = new Set(questions.map((q) => q.input_type).filter(Boolean));
    return Array.from(set).sort();
  }, [questions]);

  const filtersActive = fType !== "all" || fRequired !== "all" || fVisibility !== "all" || fStatus !== "all" || search.trim() !== "";
  const canReorder = !filtersActive; // reorder only meaningful on the full section list

  const gridItems = useMemo(() => {
    let list = questions.filter((q) => q.step === activeStep && (q.section || "General") === activeSection);
    if (fType !== "all") list = list.filter((q) => q.input_type === fType);
    if (fRequired !== "all") list = list.filter((q) => (fRequired === "required" ? q.required : !q.required));
    if (fVisibility !== "all") list = list.filter((q) => (fVisibility === "visible" ? q.visible : !q.visible));
    if (fStatus !== "all") list = list.filter((q) => (fStatus === "active" ? q.active : !q.active));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((q) =>
        (q.label || "").toLowerCase().includes(s) ||
        (q.field_key || "").toLowerCase().includes(s) ||
        (q.formula_variable || "").toLowerCase().includes(s));
    }
    return list.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [questions, activeStep, activeSection, fType, fRequired, fVisibility, fStatus, search]);

  const toggle = async (q, field) => {
    try {
      const updated = await siUpdateQuestion(q.id, { ...q, [field]: !q[field] });
      setQuestions((prev) => prev.map((x) => (x.id === q.id ? updated : x)));
    } catch { toast.error("Update failed"); }
  };

  const move = async (idx, dir) => {
    if (!canReorder) return;
    const j = idx + dir;
    if (j < 0 || j >= gridItems.length) return;
    const a = gridItems[idx], b = gridItems[j];
    const ao = a.display_order || 0, bo = b.display_order || 0;
    setQuestions((prev) => prev.map((x) => x.id === a.id ? { ...x, display_order: bo } : x.id === b.id ? { ...x, display_order: ao } : x));
    try {
      await siReorderQuestions([{ id: a.id, display_order: bo }, { id: b.id, display_order: ao }]);
    } catch { toast.error("Reorder failed"); load(); }
  };

  const save = async () => {
    if (!editing.label || !editing.field_key) { toast.error("Label and field key are required."); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      ["min_value", "max_value", "default_value"].forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (typeof payload.options === "string") payload.options = payload.options.split("\n").map((s) => s.trim()).filter(Boolean);
      if (editing.id) {
        const u = await siUpdateQuestion(editing.id, payload);
        setQuestions((prev) => prev.map((x) => (x.id === editing.id ? u : x)));
        toast.success("Question updated");
      } else {
        const c = await siCreateQuestion(payload);
        setQuestions((prev) => [...prev, c]);
        toast.success("Question created");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await siDeleteQuestion(deleteTarget.id);
      setQuestions((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
    finally { setDeleteTarget(null); }
  };

  const openNew = () => setEditing({
    ...EMPTY,
    step: activeStep,
    section: activeSection || "General",
    display_order: (totalsByStep[activeStep] || 0) * 10 + 10,
  });

  const activeSectionLabel = sections.find((s) => s.key === activeSection)?.label || "Questions";

  return (
    <div data-testid="si-questions-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>SmartInspect ROI Questions</h1>
        <Button data-testid="si-add-question-btn" onClick={openNew} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Question
        </Button>
      </div>
      <p className="text-xs text-[#9CA3AF] mb-4">
        Configure the questions that drive the ROI assessment, organised by Step → Section. Renaming a label never changes the internal field key or formula variable.
      </p>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#025979]" /></div>
      ) : (
        <>
          {/* LEVEL 1 — Step tabs */}
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="si-step-tabs">
            {STEPS.map((s) => {
              const active = activeStep === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`si-step-tab-${s.key}`}
                  onClick={() => setActiveStep(s.key)}
                  className={`flex items-center gap-2 px-4 py-2 text-xs rounded-sm border transition-colors ${active ? "bg-[#025979] text-white border-[#025979]" : "bg-white text-[#374151] border-[#E5E7EB] hover:bg-gray-50"}`}
                >
                  <span className="font-semibold">{s.num}</span>
                  <span className={active ? "text-white/80" : "text-[#9CA3AF]"}>· {s.name}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${active ? "bg-white/25" : "bg-gray-100 text-[#6B7280]"}`}>
                    {totalsByStep[s.key] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-4">
            {/* LEVEL 2 — Section list */}
            <div className="w-56 shrink-0" data-testid="si-section-list">
              <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-[#E5E7EB] text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">Sections</div>
                <div className="max-h-[520px] overflow-y-auto">
                  {sections.map((s) => {
                    const active = activeSection === s.key;
                    return (
                      <button
                        key={s.key}
                        data-testid={`si-section-pill-${s.key}`}
                        onClick={() => setActiveSection(s.key)}
                        className={`w-full text-left px-3 py-2 text-xs border-b border-[#F3F4F6] transition-colors flex items-center justify-between gap-2 ${active ? "bg-[#E0F2FE] text-[#025979] font-semibold border-l-2 border-l-[#025979]" : "text-[#374151] hover:bg-gray-50"}`}
                      >
                        <span className="leading-tight">{s.label}</span>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9px] ${active ? "bg-[#025979] text-white" : "bg-gray-100 text-[#6B7280]"}`}>{s.count}</span>
                      </button>
                    );
                  })}
                  {sections.length === 0 && (
                    <div className="px-3 py-6 text-center text-[11px] text-[#9CA3AF]">No sections yet.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Right column: filters + grid */}
            <div className="flex-1 min-w-0">
              {/* Filters */}
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="si-question-filters">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input data-testid="si-filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search label, field key, formula variable…" className="rounded-sm text-xs h-8 pl-8" />
                </div>
                <Select value={fType} onValueChange={setFType}>
                  <SelectTrigger data-testid="si-filter-type" className="rounded-sm text-xs h-8 w-36"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {availableTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fRequired} onValueChange={setFRequired}>
                  <SelectTrigger data-testid="si-filter-required" className="rounded-sm text-xs h-8 w-32"><SelectValue placeholder="Required" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="required">Required</SelectItem>
                    <SelectItem value="optional">Optional</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={fVisibility} onValueChange={setFVisibility}>
                  <SelectTrigger data-testid="si-filter-visibility" className="rounded-sm text-xs h-8 w-32"><SelectValue placeholder="Visibility" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="visible">Visible</SelectItem>
                    <SelectItem value="hidden">Hidden</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={fStatus} onValueChange={setFStatus}>
                  <SelectTrigger data-testid="si-filter-status" className="rounded-sm text-xs h-8 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {filtersActive && (
                  <button data-testid="si-clear-filters" onClick={() => { setSearch(""); setFType("all"); setFRequired("all"); setFVisibility("all"); setFStatus("all"); }} className="text-xs text-[#025979] hover:text-[#03738C] font-medium">Clear</button>
                )}
              </div>

              {/* Question Grid */}
              <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="si-question-table">
                <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
                  <p className="text-xs font-medium text-[#111827]">
                    {STEP_MAP[activeStep]?.num} · {activeSectionLabel}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF]">{gridItems.length} question{gridItems.length !== 1 ? "s" : ""}{filtersActive ? " (filtered)" : ""}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#E5E7EB] bg-white">
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Order</th>
                        <th className="text-left px-3 py-2 text-[#6B7280] font-medium min-w-[220px]">Question</th>
                        <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-32">Section</th>
                        <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-24">Answer Type</th>
                        <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-20">Unit</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-20">Required</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-20">Visibility</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Status</th>
                        <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody data-testid="si-question-tbody">
                      {gridItems.map((q, idx) => (
                        <tr key={q.id} data-testid={`si-question-row-${q.field_key}`} className="border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors align-top">
                          <td className="px-2 py-2.5">
                            <div className="flex items-center justify-center gap-0.5">
                              <span className="text-[#9CA3AF] text-[10px] w-4 text-center">{q.display_order || idx + 1}</span>
                              {canReorder && (
                                <div className="flex flex-col">
                                  <button data-testid={`si-moveup-${q.field_key}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed" title="Move up"><ArrowUp className="w-3 h-3" /></button>
                                  <button data-testid={`si-movedown-${q.field_key}`} onClick={() => move(idx, 1)} disabled={idx === gridItems.length - 1} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed" title="Move down"><ArrowDown className="w-3 h-3" /></button>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-[#111827]">
                            <div className="leading-snug">{q.label}</div>
                            <div className="text-[9px] text-[#9CA3AF] font-mono mt-0.5">
                              {q.field_key}{q.formula_variable ? ` · ${q.formula_variable}` : ""}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{humanize(q.section)}</td>
                          <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{q.input_type}</td>
                          <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{q.unit || <span className="text-[#D1D5DB]">—</span>}</td>
                          <td className="px-2 py-2.5 text-center"><RequiredBadge required={!!q.required} /></td>
                          <td className="px-2 py-2.5 text-center"><VisibilityBadge visible={!!q.visible} /></td>
                          <td className="px-2 py-2.5 text-center"><StatusBadge active={!!q.active} /></td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            <button data-testid={`si-edit-${q.field_key}`} onClick={() => setEditing({ ...q, options: (q.options || []).join("\n") })} className="text-[#025979] hover:text-[#03738C] mr-2" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                            <button data-testid={`si-delete-${q.field_key}`} onClick={() => setDeleteTarget(q)} className="text-red-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                          </td>
                        </tr>
                      ))}
                      {gridItems.length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-[#9CA3AF]">
                            {filtersActive ? "No questions match the current filters." : "No questions in this section yet."}
                            {!filtersActive && <button onClick={openNew} className="text-[#025979] hover:text-[#03738C] ml-1 font-medium">Add one</button>}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit / Create modal — retains all SmartInspect-specific fields */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="si-question-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Question" : "New Question"}</DialogTitle>
            <DialogDescription>Field key and formula variable are locked once a question is created to protect ROI formulas and historical data.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Question Label" span><Input data-testid="si-q-label" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} /></Field>
              <Field label="Internal Field Key (immutable once created)"><Input data-testid="si-q-fieldkey" value={editing.field_key} disabled={!!editing.id} onChange={(e) => setEditing({ ...editing, field_key: e.target.value })} /></Field>
              <Field label="Formula Variable (immutable once created)"><Input data-testid="si-q-formulavar" value={editing.formula_variable || ""} disabled={!!editing.id} onChange={(e) => setEditing({ ...editing, formula_variable: e.target.value })} /></Field>
              <Field label="Step">
                <Select value={editing.step} onValueChange={(v) => setEditing({ ...editing, step: v })}>
                  <SelectTrigger data-testid="si-q-step"><SelectValue /></SelectTrigger>
                  <SelectContent>{STEP_ORDER.map((s) => <SelectItem key={s} value={s}>{STEP_TITLES[s]}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Section"><Input data-testid="si-q-section" value={editing.section} onChange={(e) => setEditing({ ...editing, section: e.target.value })} /></Field>
              <Field label="Input Type">
                <Select value={editing.input_type} onValueChange={(v) => setEditing({ ...editing, input_type: v })}>
                  <SelectTrigger data-testid="si-q-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.input_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Unit"><Input value={editing.unit || ""} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} /></Field>
              <Field label="Default Value"><Input value={editing.default_value ?? ""} onChange={(e) => setEditing({ ...editing, default_value: e.target.value })} /></Field>
              <Field label="Min Value"><Input value={editing.min_value ?? ""} onChange={(e) => setEditing({ ...editing, min_value: e.target.value })} /></Field>
              <Field label="Max Value"><Input value={editing.max_value ?? ""} onChange={(e) => setEditing({ ...editing, max_value: e.target.value })} /></Field>
              <Field label="Placeholder" span><Input value={editing.placeholder || ""} onChange={(e) => setEditing({ ...editing, placeholder: e.target.value })} /></Field>
              <Field label="Description" span><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
              <Field label="Help Text / Tooltip" span><Input value={editing.help_text || ""} onChange={(e) => setEditing({ ...editing, help_text: e.target.value })} /></Field>
              <Field label="Options (one per line — for dropdown/radio/multi-select)" span>
                <Textarea data-testid="si-q-options" rows={3} value={editing.options || ""} onChange={(e) => setEditing({ ...editing, options: e.target.value })} />
              </Field>
              <Field label="ROI Profile (optional)" span><Input value={editing.roi_profile || ""} onChange={(e) => setEditing({ ...editing, roi_profile: e.target.value })} /></Field>
              <div className="col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {["required", "active", "visible", "customer_visible", "customer_editable", "consultant_editable", "defaulted_by_admin"].map((f) => (
                  <label key={f} className="flex items-center gap-2 text-xs text-slate-600">
                    <Switch data-testid={`si-q-${f}`} checked={!!editing[f]} onCheckedChange={(v) => setEditing({ ...editing, [f]: v })} /> {f.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="si-q-save" onClick={save} disabled={saving}>{saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="si-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Question</DialogTitle>
            <DialogDescription>
              Delete "{deleteTarget?.label}"? Historical assessment values are preserved. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="si-confirm-delete-btn" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
